import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiRefreshCw,
  FiDollarSign,
  FiClock,
  FiFileText,
  FiX,
  FiCheck,
  FiSettings,
  FiHome,
  FiMonitor
} = FiIcons;

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pricing_structure: 'simple',
    simple_pricing: {
      application_fee: '',
      service_fee: '',
      taxes: ''
    },
    detailed_pricing: {
      online: {},
      home: {}
    },
    processing_time: '',
    last_date: '',
    required_documents: [''],
    icon: 'FiFileText',
    color: 'from-blue-500 to-blue-600',
    category: 'application'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('services_dy3xr')
        .select('*')
        .order('name');

      if (error) throw error;
      
      const uniqueServices = data ? data.filter((service, index, self) => 
        index === self.findIndex(s => s.name === service.name)
      ) : [];
      
      setServices(uniqueServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchServices();
      toast.success('Services refreshed');
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('simple_')) {
      const field = name.replace('simple_', '');
      setFormData(prev => ({
        ...prev,
        simple_pricing: {
          ...prev.simple_pricing,
          [field]: value === '' ? '' : Number(value)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDocumentChange = (index, value) => {
    const newDocuments = [...formData.required_documents];
    newDocuments[index] = value;
    setFormData(prev => ({ ...prev, required_documents: newDocuments }));
  };

  const addDocumentField = () => {
    setFormData(prev => ({
      ...prev,
      required_documents: [...prev.required_documents, '']
    }));
  };

  const removeDocumentField = (index) => {
    const newDocuments = [...formData.required_documents];
    newDocuments.splice(index, 1);
    setFormData(prev => ({ ...prev, required_documents: newDocuments }));
  };

  const handleAddService = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      pricing_structure: 'simple',
      simple_pricing: {
        application_fee: '',
        service_fee: '',
        taxes: ''
      },
      detailed_pricing: {
        online: {},
        home: {}
      },
      processing_time: '',
    last_date: '',
    required_documents: [''],
      icon: 'FiFileText',
      color: 'from-blue-500 to-blue-600',
      category: 'application'
    });
    setShowAddModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    
    const hasDetailedPricing = service.detailed_pricing && 
      (Object.keys(service.detailed_pricing.online || {}).length > 0 || 
       Object.keys(service.detailed_pricing.home || {}).length > 0);
    
    setFormData({
      id: service.id,
      name: service.name,
      description: service.description,
      pricing_structure: hasDetailedPricing ? 'detailed' : 'simple',
      simple_pricing: {
        application_fee: service.application_fee || '',
        service_fee: service.service_fee || '',
        taxes: service.taxes || ''
      },
      detailed_pricing: service.detailed_pricing || {
        online: {},
        home: {}
      },
      processing_time: service.processing_time,
      last_date: service.last_date || '',
      required_documents: Array.isArray(service.required_documents) && service.required_documents.length > 0
        ? service.required_documents
        : [''],
      icon: service.icon,
      color: service.color,
      category: service.category || 'application'
    });
    setShowAddModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('services_dy3xr')
          .delete()
          .eq('id', serviceId);

        if (error) throw error;
        
        setServices(services.filter(service => service.id !== serviceId));
        toast.success('Service deleted successfully');
      } catch (error) {
        console.error('Error deleting service:', error);
        toast.error('Failed to delete service');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.processing_time) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (formData.pricing_structure === 'simple') {
      if (!formData.simple_pricing.application_fee.toString() || 
          !formData.simple_pricing.service_fee.toString() || 
          !formData.simple_pricing.taxes.toString()) {
        toast.error('Please fill all pricing fields');
        return;
      }
    } else {
      const hasOnlinePricing = Object.keys(formData.detailed_pricing.online).length > 0;
      const hasHomePricing = Object.keys(formData.detailed_pricing.home).length > 0;
      
      if (!hasOnlinePricing && !hasHomePricing) {
        toast.error('Please add at least one pricing category');
        return;
      }
    }

    const filteredDocuments = formData.required_documents.filter(doc => doc.trim() !== '');
    if (filteredDocuments.length === 0) {
      toast.error('Please add at least one required document');
      return;
    }

    try {
      let serviceData = {
        name: formData.name,
        description: formData.description,
        processing_time: formData.processing_time,
        last_date: formData.last_date || null,
        required_documents: filteredDocuments,
        icon: formData.icon,
        color: formData.color,
        category: formData.category,
        updated_at: new Date().toISOString()
      };
      
      if (formData.pricing_structure === 'simple') {
        serviceData = {
          ...serviceData,
          application_fee: formData.simple_pricing.application_fee,
          service_fee: formData.simple_pricing.service_fee,
          taxes: formData.simple_pricing.taxes,
          pricing_structure: 'simple'
        };
      } else {
        serviceData = {
          ...serviceData,
          detailed_pricing: formData.detailed_pricing,
          pricing_structure: 'detailed',
          application_fee: 0,
          service_fee: 0,
          taxes: 0
        };
      }

      let result;
      if (editingService) {
        const { data, error } = await supabase
          .from('services_dy3xr')
          .update(serviceData)
          .eq('id', editingService.id)
          .select();

        if (error) throw error;
        result = data[0];
        
        setServices(services.map(service => 
          service.id === editingService.id ? result : service
        ));
        
        toast.success('Service updated successfully');
      } else {
        const { data, error } = await supabase
          .from('services_dy3xr')
          .insert(serviceData)
          .select();

        if (error) throw error;
        result = data[0];
        
        setServices([...services, result]);
        
        toast.success('Service created successfully');
      }
      
      setShowAddModal(false);
      
      setFormData({
        name: '',
        description: '',
        pricing_structure: 'simple',
        simple_pricing: {
          application_fee: '',
          service_fee: '',
          taxes: ''
        },
        detailed_pricing: {
          online: {},
          home: {}
        },
        processing_time: '',
    last_date: '',
    required_documents: [''],
        icon: 'FiFileText',
        color: 'from-blue-500 to-blue-600',
        category: 'application'
      });
    } catch (error) {
      console.error('Error saving service:', error);
      const message = error?.message || error?.error_description || 'Unknown error';
      toast.error(`Failed to save service: ${message}`);
    }
  };

  const pricingCategories = [
    'OC Male',
    'OC Female', 
    'EWS/OBC Male',
    'EWS/OBC Female',
    'SC/ST/PWD Male/Female',
    'All Categories',
    'General',
    'BC',
    'SC/ST',
    'Engineering â€“ General',
    'Engineering â€“ BC',
    'Engineering â€“ SC/ST',
    'Agriculture â€“ General',
    'Agriculture â€“ BC', 
    'Agriculture â€“ SC/ST',
    'Both Eng + Agri â€“ General',
    'Both Eng + Agri â€“ BC',
    'Both Eng + Agri â€“ SC/ST'
  ];

  const addPricingCategory = (serviceType) => {
    const categoryName = prompt('Enter category name (e.g., "OC Male", "SC/ST", etc.):');
    if (categoryName && categoryName.trim()) {
      setFormData(prev => ({
        ...prev,
        detailed_pricing: {
          ...prev.detailed_pricing,
          [serviceType]: {
            ...prev.detailed_pricing[serviceType],
            [categoryName.trim()]: {
              examFee: 0,
              serviceCharge: 0,
              bankCharge: 50,
              total: 50
            }
          }
        }
      }));
    }
  };

  const removePricingCategory = (serviceType, category) => {
    setFormData(prev => {
      const newPricing = { ...prev.detailed_pricing };
      delete newPricing[serviceType][category];
      return {
        ...prev,
        detailed_pricing: newPricing
      };
    });
  };

  const updateCategoryPricing = (serviceType, category, field, value) => {
    const numValue = value === '' ? 0 : Number(value);
    setFormData(prev => {
      const categoryData = { ...prev.detailed_pricing[serviceType][category] };
      categoryData[field] = numValue;
      
      if (field !== 'total') {
        categoryData.total = (categoryData.examFee || 0) + 
                           (categoryData.serviceCharge || 0) + 
                           (categoryData.bankCharge || 0);
      }
      
      return {
        ...prev,
        detailed_pricing: {
          ...prev.detailed_pricing,
          [serviceType]: {
            ...prev.detailed_pricing[serviceType],
            [category]: categoryData
          }
        }
      };
    });
  };

  const availableIcons = [
    { name: 'FiFileText', component: FiFileText },
    { name: 'FiDollarSign', component: FiDollarSign },
    { name: 'FiClock', component: FiClock },
    { name: 'FiSettings', component: FiSettings },
  ];

  const colorSchemes = [
    { name: 'Blue', value: 'from-blue-500 to-blue-600' },
    { name: 'Green', value: 'from-green-500 to-green-600' },
    { name: 'Yellow', value: 'from-yellow-500 to-yellow-600' },
    { name: 'Purple', value: 'from-purple-500 to-purple-600' },
    { name: 'Red', value: 'from-red-500 to-red-600' },
    { name: 'Pink', value: 'from-pink-500 to-pink-600' },
    { name: 'Indigo', value: 'from-indigo-500 to-indigo-600' },
    { name: 'Teal', value: 'from-teal-500 to-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Service Management</h1>
              <p className="text-gray-600 mt-1">Manage service fees, documents, and processing times</p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center"
              >
                <SafeIcon
                  icon={FiRefreshCw}
                  className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button
                onClick={handleAddService}
                className="flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-2" />
                Add New Service
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : services.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditService(service)}
                        className="text-gray-600 hover:text-primary-600"
                      >
                        <SafeIcon icon={FiEdit} className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteService(service.id)}
                        className="text-gray-600 hover:text-red-600"
                      >
                        <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className={`bg-gradient-to-r ${service.color} text-white p-4 rounded-lg mb-4`}>
                    <div className="flex items-center mb-2">
                      <SafeIcon icon={FiIcons[service.icon]} className="w-6 h-6 mr-2" />
                      <h4 className="font-semibold">Service Details</h4>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 mr-2 text-green-600" />
                      Fees
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Application Fee:</span>
                        <span className="font-semibold">â‚¹{service.application_fee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service Fee:</span>
                        <span className="font-semibold">â‚¹{service.service_fee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Taxes:</span>
                        <span className="font-semibold">{service.taxes}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-blue-600" />
                      Processing Time
                    </h4>
                    <p className="text-sm text-gray-600">{service.processing_time}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2 text-purple-600" />
                      Required Documents
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.required_documents.map((doc, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
                        >
                          {doc}
                        </span>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <SafeIcon icon={FiSettings} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
            <p className="text-gray-600 mb-6">Create your first service to get started</p>
            <Button onClick={handleAddService}>
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Service
            </Button>
          </Card>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Service Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., College Application"
                  required
                />
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the service"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={3}
                    required
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2 text-green-600" />
                  Service Fees
                </h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pricing Structure
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="pricing_structure"
                        value="simple"
                        checked={formData.pricing_structure === 'simple'}
                        onChange={handleInputChange}
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Simple Pricing</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="pricing_structure"
                        value="detailed"
                        checked={formData.pricing_structure === 'detailed'}
                        onChange={handleInputChange}
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Detailed Pricing (by Category)</span>
                    </label>
                  </div>
                </div>

                {formData.pricing_structure === 'simple' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Application Fee (â‚¹)"
                      name="simple_application_fee"
                      type="number"
                      value={formData.simple_pricing.application_fee}
                      onChange={handleInputChange}
                      placeholder="0"
                      required
                    />
                    <Input
                      label="Service Fee (â‚¹)"
                      name="simple_service_fee"
                      type="number"
                      value={formData.simple_pricing.service_fee}
                      onChange={handleInputChange}
                      placeholder="0"
                      required
                    />
                    <Input
                      label="Taxes (%)"
                      name="simple_taxes"
                      type="number"
                      value={formData.simple_pricing.taxes}
                      onChange={handleInputChange}
                      placeholder="0"
                      required
                    />
                  </div>
                )}

                {formData.pricing_structure === 'detailed' && (
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="font-medium text-gray-900 flex items-center">
                          <SafeIcon icon={FiMonitor} className="w-4 h-4 mr-2 text-green-600" />
                          Online Service Pricing
                        </h5>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addPricingCategory('online')}
                        >
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                          Add Category
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        {Object.entries(formData.detailed_pricing.online).map(([category, pricing]) => (
                          <div key={category} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h6 className="font-medium text-gray-800">{category}</h6>
                              <button
                                type="button"
                                onClick={() => removePricingCategory('online', category)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Exam Fee (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.examFee || ''}
                                  onChange={(e) => updateCategoryPricing('online', category, 'examFee', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Service Charge (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.serviceCharge || ''}
                                  onChange={(e) => updateCategoryPricing('online', category, 'serviceCharge', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Bank Charges and Taxes (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.bankCharge || ''}
                                  onChange={(e) => updateCategoryPricing('online', category, 'bankCharge', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="50"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Total (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.total || ''}
                                  readOnly
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 font-semibold"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {Object.keys(formData.detailed_pricing.online).length === 0 && (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No online pricing categories added yet
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="font-medium text-gray-900 flex items-center">
                          <SafeIcon icon={FiHome} className="w-4 h-4 mr-2 text-blue-600" />
                          Home Service Pricing
                        </h5>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addPricingCategory('home')}
                        >
                          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                          Add Category
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        {Object.entries(formData.detailed_pricing.home).map(([category, pricing]) => (
                          <div key={category} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h6 className="font-medium text-gray-800">{category}</h6>
                              <button
                                type="button"
                                onClick={() => removePricingCategory('home', category)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Exam Fee (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.examFee || ''}
                                  onChange={(e) => updateCategoryPricing('home', category, 'examFee', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Service Charge (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.serviceCharge || ''}
                                  onChange={(e) => updateCategoryPricing('home', category, 'serviceCharge', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Bank Charges and Taxes (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.bankCharge || ''}
                                  onChange={(e) => updateCategoryPricing('home', category, 'bankCharge', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="50"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Total (â‚¹)
                                </label>
                                <input
                                  type="number"
                                  value={pricing.total || ''}
                                  readOnly
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 font-semibold"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {Object.keys(formData.detailed_pricing.home).length === 0 && (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No home pricing categories added yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-blue-600" />
                  Processing Time
                </label>
                <input
                  name="processing_time"
                  value={formData.processing_time}
                  onChange={handleInputChange}
                  placeholder="e.g., 3-5 business days"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2 text-purple-600" />
                  Required Documents
                </label>
                {formData.required_documents.map((doc, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={doc}
                      onChange={(e) => handleDocumentChange(index, e.target.value)}
                      placeholder="Document name"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {formData.required_documents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDocumentField(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addDocumentField}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  + Add Document
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingService ? 'Update Service' : 'Create Service'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;



